import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaCallIntelligenceWritebackV1Service, serviceName } from '../service';
import { CallWritebackStatusV2IO, ServiceQueries } from '../types';
import { useCallIntelligenceWritebackV1Queries } from './use-call-intelligence-writeback-v1-queries';
import {
  UseCallIntelligenceWritebackV1QueryEndpointArgs,
  useCallIntelligenceWritebackV1Query,
} from './use-call-intelligence-writeback-v1-query';

/**
 * A hook that returns a query for the `CallWritebackStatusV2` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useCallWritebackStatusV2Query = <E = unknown, D = CallWritebackStatusV2IO['output']>(
  args: UseCallIntelligenceWritebackV1QueryEndpointArgs<'CallWritebackStatusV2', E, D>
) =>
  useCallIntelligenceWritebackV1Query({
    endpointName: 'CallWritebackStatusV2',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `CallWritebackStatusV2` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useCallWritebackStatusV2Queries = <E = unknown, D = CallWritebackStatusV2IO['output']>(
  argsArr: UseCallIntelligenceWritebackV1QueryEndpointArgs<'CallWritebackStatusV2', E, D>[]
) =>
  useCallIntelligenceWritebackV1Queries(
    argsArr.map((args) => ({
      endpointName: 'CallWritebackStatusV2',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `CallWritebackStatusV2` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchCallWritebackStatusV2 = () => {
  const queryClient = useQueryClient();

  const fetchCallWritebackStatusV2 = async <E = unknown, D = CallWritebackStatusV2IO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseCallIntelligenceWritebackV1QueryEndpointArgs<'CallWritebackStatusV2', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      CallWritebackStatusV2IO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'CallWritebackStatusV2'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        CallWritebackStatusV2IO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'CallWritebackStatusV2'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'CallWritebackStatusV2'>({
          serviceName,
          endpointName: 'CallWritebackStatusV2',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaCallIntelligenceWritebackV1Service.CallWritebackStatusV2(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchCallWritebackStatusV2;
};
